import { ReactNode, CSSProperties, Dispatch, SetStateAction } from 'react';
import { useHistory } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import DateUtil from 'util/DateUtil';
import { highlightChar } from 'util/TestNotification/TestNotificationUtil';
import { type ITheme } from 'theme/StyledComponentsTheme';
import { type ITestNotificationHistoryPageProps } from 'component/page/TestNotificationHistoryPage';
import { type ITestNotification } from 'redux/container/TestNotificationHistoryPageContainer';

import DropdownBase from 'component/ui/dropdown/DropdownBase';
import Switch from 'component/ui/switch/Switch';
import Tooltip from 'component/ui/tooltip/Tooltip';
import { AlarmIcon, OpenInNewIcon } from 'component/ui/icons';

interface ITestNotificationDropdownFragmentProps {
  showDropdown: boolean;
  setShowDropdown: Dispatch<SetStateAction<boolean>>;
  isSwitchToggled: boolean;
  setIsSwitchToggled: Dispatch<SetStateAction<boolean>>;
  notiCount: number;
  filteredByReadStatus: ITestNotification[];
  todaysNotiList: ITestNotification[];
  beforeTodayNotiList: ITestNotification[];
  handleReadAll: () => void;
}

function TestNotificationDropdownFragment({
  showDropdown,
  setShowDropdown,
  isSwitchToggled,
  setIsSwitchToggled,
  notiCount,
  filteredByReadStatus,
  todaysNotiList,
  beforeTodayNotiList,
  handleReadAll,
}: ITestNotificationDropdownFragmentProps) {
  const intl = useIntl();

  return (
    <DropdownBase
      show={showDropdown}
      setShow={setShowDropdown}
      style={{ marginRight: 11 }}
      dropdownDirection="right"
      contentCloseReason="clickAway"
      anchorProps={{ style: { border: 'none', minWidth: 0, padding: 0 } }}
      contentProps={{ style: { border: 'none' } }}
      label={
        <>
          <NotiIconWrapper />
          {!!notiCount && <CountBadge notiCount={notiCount} />}
        </>
      }>
      <ContentWrapper>
        <HeaderWrapper>
          <HeaderLeftWrapper>
            <ContentText>
              {intl.formatMessage(INTL_MAP.NOTIFICATIONS)}
            </ContentText>
          </HeaderLeftWrapper>
          <HeaderRightWrapper>
            <UnreadItemToggle
              isSwitchToggled={isSwitchToggled}
              setIsSwitchToggled={setIsSwitchToggled}
            />
            <Tooltip
              option={{ maxWidth: 200 }}
              title={intl.formatMessage(INTL_MAP.VIEW_ALL_NOTI_IN_A_NEW_TAB)}>
              <BgWrapper>
                <OpenInNewIconWrapper
                  onClick={() => {
                    window.open(
                      `${window.location.origin}/test-notification-history`,
                      '_blank'
                    );
                  }}
                />
              </BgWrapper>
            </Tooltip>
          </HeaderRightWrapper>
        </HeaderWrapper>
        <ContentBody>
          <VisibilityWrapper show={!!filteredByReadStatus.length}>
            <ItemDivider>
              <ItemDividerText hide={!todaysNotiList.length}>
                {intl.formatMessage(INTL_MAP.TODAY)}
              </ItemDividerText>
              <ItemDividerText
                style={{ textDecorationLine: 'underline', cursor: 'pointer' }}
                onClick={handleReadAll}>
                {intl.formatMessage(INTL_MAP.MARK_ALL_AS_READ)}
              </ItemDividerText>
            </ItemDivider>
            {todaysNotiList.map(
              ({
                shortTid,
                tid,
                createdAt,
                patientName,
                notificationMessage,
                isHospitalUserReadNotification,
                isNotificationSentToHospitalUser,
              }) => (
                <NotiItem
                  key={tid}
                  tid={tid}
                  shortTid={shortTid}
                  createdAt={createdAt}
                  patientName={patientName}
                  notificationMessage={notificationMessage}
                  isHospitalUserReadNotification={
                    isHospitalUserReadNotification
                  }
                  isNotificationSentToHospitalUser={
                    isNotificationSentToHospitalUser
                  }
                />
              )
            )}
            {!!todaysNotiList.length && !!beforeTodayNotiList.length && (
              <>
                <ContentDivider />
                <ItemDivider style={{ paddingTop: 6 }}>
                  <ItemDividerText>
                    {intl.formatMessage(INTL_MAP.PREVIOUS)}
                  </ItemDividerText>
                </ItemDivider>
              </>
            )}
            {beforeTodayNotiList.map(
              ({
                shortTid,
                tid,
                createdAt,
                patientName,
                notificationMessage,
                isHospitalUserReadNotification,
                isNotificationSentToHospitalUser,
              }) => (
                <NotiItem
                  key={tid}
                  tid={tid}
                  shortTid={shortTid}
                  createdAt={createdAt}
                  patientName={patientName}
                  notificationMessage={notificationMessage}
                  isHospitalUserReadNotification={
                    isHospitalUserReadNotification
                  }
                  isNotificationSentToHospitalUser={
                    isNotificationSentToHospitalUser
                  }
                />
              )
            )}
            <ShowMoreNoties />
          </VisibilityWrapper>
          <VisibilityWrapper show={!filteredByReadStatus.length}>
            <TextWrapper>
              <ItemDividerText style={{ fontWeight: 500 }}>
                {intl.formatMessage(INTL_MAP.NO_NOTI_RECEIVED)}
              </ItemDividerText>
            </TextWrapper>
          </VisibilityWrapper>
        </ContentBody>
      </ContentWrapper>
    </DropdownBase>
  );
}

export default TestNotificationDropdownFragment;

// styles
const CountBadge = styled.div<{ notiCount: number }>`
  position: absolute;
  width: 20px;
  height: 13px;
  top: -3px;
  left: 8px;
  border-radius: 100px;

  font-weight: 700;
  font-size: 10px;
  text-align: center;
  line-height: 140%;
  background-color: ${({ theme }) => theme.color.RED};
  color: ${({ theme }) => theme.color.WHITE};
  ${({ notiCount }) => `::after { content: '${notiCount}' }`}
`;

const BgWrapper = styled.div`
  display: flex;
  :hover {
    background-color: ${({ theme }) => theme.color.COOL_GRAY_30};
  }
`;

const ContentWrapper = styled.div`
  width: 370px;
  max-height: calc(100vh - 120px);
  border-radius: 6px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
`;
export const ContentDivider = styled.div<{ width?: number }>`
  width: ${({ width }) => width || 330}px;
  height: 1px;
  border-bottom: 1px solid ${({ theme }) => theme.color.COOL_GRAY_40};
`;
export const ContentBody = styled.div`
  align-items: center;
  flex-direction: column;
  display: flex;
  overflow-y: scroll;
  padding-bottom: 24px;
  gap: 12px;
  &::-webkit-scrollbar-track {
    border-radius: 6px;
  }
`;
export const ContentText = styled.div<{ notiCount?: number }>`
  font-size: 14px;
  font-weight: 700;
  line-height: 130%;
  color: ${({ theme }) => theme.color.COOL_GRAY_90};

  ::after {
    content: ${({ notiCount }) =>
      typeof notiCount === 'number' && `" ${notiCount}"`};
    color: ${({ theme }) => theme.color.BLUE_70};
  }
`;

export const HeaderWrapper = styled.div<{ width?: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: ${({ width }) => width || 330}px;
  height: 20px;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.COOL_GRAY_40};
`;
export const HeaderLeftWrapper = styled.div`
  display: flex;
  gap: 12px;
`;
export const HeaderRightWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const ItemWrapper = styled.div<{
  width?: number;
  isHospitalUserReadNotification: boolean;
}>`
  width: ${({ width }) => width || 329}px;
  padding: 6px;
  margin-left: 14px;
  font-size: 12px;
  font-weight: 500;
  line-height: 130%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  border-radius: 4px;
  :hover {
    background-color: ${({ theme }) => theme.color.COOL_GRAY_30};
  }
  color: ${({ theme, isHospitalUserReadNotification }) =>
    isHospitalUserReadNotification
      ? theme.color.COOL_GRAY_60
      : theme.color.COOL_GRAY_90};
`;
const ItemTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
const ItemTitle = styled.div`
  font-weight: 700;
`;
const ItemBody = styled.div``;
const ItemTime = styled.div<{ isHospitalUserReadNotification: boolean }>`
  margin-top: 2px;
  font-size: 11px;
  color: ${({ theme, isHospitalUserReadNotification }) =>
    isHospitalUserReadNotification
      ? theme.color.COOL_GRAY_60
      : theme.color.COOL_GRAY_70};
`;
export const ItemDivider = styled.div<{ width?: number }>`
  width: ${({ width }) => width || 330}px;
  height: 16px;
  padding: 18px 5px 2px 20px;
  display: flex;
  justify-content: space-between;
`;
export const ItemDividerText = styled.div<{
  hide?: boolean;
  style?: CSSProperties;
}>`
  font-size: 12px;
  font-weight: 700;
  line-height: 130%;
  visibility: ${({ hide }) => (hide ? 'hidden' : 'inherit')};
  color: ${({ theme }) => theme.color.COOL_GRAY_70};
`;

export const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 364px;
`;

export const NotificationBadge = styled.div<{
  isHospitalUserReadNotification?: boolean;
}>`
  width: 8px;
  height: 8px;
  border-radius: 100%;
  margin: 4px;
  background-color: ${({ theme, isHospitalUserReadNotification }) =>
    isHospitalUserReadNotification
      ? theme.color.COOL_GRAY_50
      : theme.color.RED_70};
`;

const ShowMoreNotiesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 55px 4px 54px;
  font-size: 12px;
  line-height: 130%;
  text-align: center;
`;
const ShowMoreNotiesText = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.color.COOL_GRAY_90};
`;
const ShowMoreNotiesButton = styled.div`
  font-weight: 700;
  text-decoration-line: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.color.BLUE_70};
`;

const UnreadItemToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 130%;
  color: ${({ theme }) => theme.color.COOL_GRAY_70};
`;

const IconWrapper = styled.div<{ isHospitalUserReadNotification: boolean }>`
  display: flex;
  gap: 6px;
  svg {
    width: 16px;
    height: 16px;
  }
  & path {
    fill: ${({ theme, isHospitalUserReadNotification }) =>
      isHospitalUserReadNotification
        ? theme.color.COOL_GRAY_50
        : theme.color.SVE_70};
  }
`;
const NotiIconWrapper = styled(AlarmIcon)`
  width: 24px;
  height: 24px;
`;
const OpenInNewIconWrapper = styled(OpenInNewIcon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

// components
interface INotiItem extends ITestNotification {
  width?: number;
}
export const NotiItem = ({
  tid,
  shortTid,
  patientName,
  createdAt,
  notificationMessage,
  isHospitalUserReadNotification,
  width,
}: INotiItem) => {
  const theme = useTheme() as ITheme;
  const intl = useIntl();

  return (
    <ItemWrapper
      width={width}
      isHospitalUserReadNotification={isHospitalUserReadNotification}
      onClick={() => {
        window.location.href = `/test/${tid}?tab=general`;
      }}>
      <ItemTitleWrapper>
        <ItemTitle>{`[${patientName}(${shortTid})] ${intl.formatMessage(
          INTL_MAP.NEEDS_QUICK_CHECK_SYMPTOM
        )}`}</ItemTitle>
        <IconWrapper
          isHospitalUserReadNotification={isHospitalUserReadNotification}>
          <NotificationBadge
            isHospitalUserReadNotification={isHospitalUserReadNotification}
          />
        </IconWrapper>
      </ItemTitleWrapper>
      <ItemBody>
        {highlightChar(notificationMessage, '|', {
          color: theme.color.COOL_GRAY_60,
        })}
      </ItemBody>
      <ItemTime isHospitalUserReadNotification={isHospitalUserReadNotification}>
        {DateUtil.formatDateToKorean(new Date(createdAt))}
      </ItemTime>
    </ItemWrapper>
  );
};

export const UnreadItemToggle = ({
  isSwitchToggled,
  setIsSwitchToggled,
}: Pick<
  ITestNotificationHistoryPageProps,
  'isSwitchToggled' | 'setIsSwitchToggled'
>) => {
  const intl = useIntl();

  return (
    <UnreadItemToggleWrapper>
      {intl.formatMessage(INTL_MAP.SHOW_UNREAD_NOTI_ONLY)}
      <Switch
        onChange={() => setIsSwitchToggled((prev: boolean) => !prev)}
        checked={isSwitchToggled}
      />
    </UnreadItemToggleWrapper>
  );
};

export const ShowMoreNoties = () => {
  const history = useHistory();
  const intl = useIntl();

  return (
    <ShowMoreNotiesWrapper>
      <ShowMoreNotiesText>
        {intl.formatMessage(INTL_MAP.VIEWED_ALL_NOTI_LAST_THIRTY)}
      </ShowMoreNotiesText>
      <ShowMoreNotiesButton
        onClick={() => {
          history.push('/test-notification-history');
        }}>
        {intl.formatMessage(INTL_MAP.VIEW_ALL_NOTI)}
      </ShowMoreNotiesButton>
    </ShowMoreNotiesWrapper>
  );
};

export const VisibilityWrapper = ({
  show,
  children,
}: {
  show: boolean;
  children: ReactNode;
}) => {
  if (!show) return null;

  return <>{children}</>;
};

// INTL
export const INTL_MAP = defineMessages({
  NOTIFICATIONS: {
    id: '12-TestNotificationDropdownFragment-contentText-Notifications',
    description: '알림 / Notifications',
    defaultMessage: '알림',
  },
  VIEW_ALL_NOTI_IN_A_NEW_TAB: {
    id: '12-TestNotificationDropdownFragment-Tooltip-view-all-noti-in-a-new-tab',
    description:
      '새 탭에서 모든 알림 확인하기 / View All Notifications in a new tab',
    defaultMessage: '새 탭에서 모든 알림 확인하기',
  },
  TODAY: {
    id: '12-TestNotificationDropdownFragment-ItemDividerText-today',
    description: '오늘 / Today',
    defaultMessage: '오늘',
  },
  PREVIOUS: {
    id: '12-TestNotificationDropdownFragment-ItemDividerText-previous',
    description: '이전 / Previous',
    defaultMessage: '이전',
  },
  MARK_ALL_AS_READ: {
    id: '12-TestNotificationDropdownFragment-ItemDividerText-mark-all-as-read',
    description: '모두 읽음으로 표시 / Mark All as read',
    defaultMessage: '모두 읽음으로 표시',
  },
  NO_NOTI_RECEIVED: {
    id: '12-TestNotificationDropdownFragment-ItemDividerText-no-noti-received',
    description: '받은 긴급알림이 없습니다. / No notifications received.',
    defaultMessage: '받은 긴급알림이 없습니다.',
  },
  NEEDS_QUICK_CHECK_SYMPTOM: {
    id: '12-NotiItem-itemTitle-needs-quick-check-symptom',
    description:
      '빠른 확인이 필요한 증상이 있습니다. / Requiring immediate confirmation of symptoms.',
    defaultMessage: '빠른 확인이 필요한 증상이 있습니다.',
  },
  SHOW_UNREAD_NOTI_ONLY: {
    id: '12-UnreadItemToggle-UnreadItemToggleWrapper-show-unread-noti-only',
    description: '읽지 않은 항목만 표시 / Show unread notify only',
    defaultMessage: '읽지 않은 항목만 표시',
  },
  VIEWED_ALL_NOTI_LAST_THIRTY: {
    id: '12-ShowMoreNoties-ShowMoreNotiesText-seen-all-noti-last-thirty',
    description:
      "최근 30일 동안 받은 알림을 모두 확인했습니다. / You've viewed all notifications from past 30days.",
    defaultMessage: '최근 30일 동안 받은 알림을 모두 확인했습니다.',
  },
  VIEW_ALL_NOTI: {
    id: '12-ShowMoreNoties-ShowMoreNotiesButton-see-all-noti',
    description: '모든 알림 확인하기 / View all notifications',
    defaultMessage: '모든 알림 확인하기',
  },
  ALL: {
    id: '13-TestNotificationHistoryPage-ContentText-all',
    description: '전체 / All',
    defaultMessage: '전체',
  },
});
